import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box, Typography, Stepper, Step, StepLabel, StepContent,
  Button, Paper, Chip, CircularProgress, Tooltip
} from '@mui/material';
import {
  FlagOutlined, Code, BugReport, People, Rocket, CheckCircle
} from '@mui/icons-material';



const STAGES = [
  {
    key: 'planning',
    label: 'Planning',
    description: 'Project scoped and resources assigned',
    icon: <FlagOutlined />,
    color: '#607d8b',
    bg: '#eceff1',
  },
  {
    key: 'development',
    label: 'Development',
    description: 'Active development and coding phase',
    icon: <Code />,
    color: '#1565c0',
    bg: '#e3f2fd',
  },
  {
    key: 'sit',
    label: 'SIT',
    description: 'System Integration Testing',
    icon: <BugReport />,
    color: '#e65100',
    bg: '#fff3e0',
  },
  {
    key: 'uat',
    label: 'UAT',
    description: 'User Acceptance Testing with client',
    icon: <People />,
    color: '#6a1b9a',
    bg: '#f3e5f5',
  },
  {
    key: 'go_live',
    label: 'Go-Live',
    description: 'Production deployment',
    icon: <Rocket />,
    color: '#00695c',
    bg: '#e0f2f1',
  },
  {
    key: 'completed',
    label: 'Completed',
    description: 'Project successfully delivered',
    icon: <CheckCircle />,
    color: '#2e7d32',
    bg: '#e8f5e9',
  },
];

const baseURL = process.env.REACT_APP_API_URL || '';

export default function ProjectWorkflow({ projectId, initialStage = 'planning' }) {
  const [currentStage, setCurrentStage] = useState(initialStage);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setCurrentStage(initialStage || 'planning');
  }, [initialStage]);

  const currentIndex = STAGES.findIndex(s => s.key === currentStage);

  const handleAdvanceStage = async (targetKey) => {
    if (!window.confirm(`Move project to "${STAGES.find(s => s.key === targetKey)?.label}"? Team members will be notified.`)) return;
    setSaving(true);
    try {
      await axios.put(`${baseURL}/api/projects/${projectId}/stage`,
        { workflow_stage: targetKey }, { headers }
      );
      setCurrentStage(targetKey);
      Swal.fire({ icon: 'success', title: `Moved to ${STAGES.find(s => s.key === targetKey)?.label}!`, text: 'Team members will be notified.', timer: 1800, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', 'Failed to update stage.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>Project Workflow</Typography>
        <Typography variant="caption" color="text.secondary">
          Track and advance the project through each phase
        </Typography>
      </Box>

      {/* Current Stage Banner */}
      <Box sx={{ mb: 3 }}>
        {(() => {
          const stage = STAGES[currentIndex];
          return (
            <Paper elevation={0} sx={{
              p: 2, borderRadius: 2, bgcolor: stage.bg,
              border: `1.5px solid ${stage.color}30`,
              display: 'flex', alignItems: 'center', gap: 2
            }}>
              <Box sx={{
                color: stage.color, bgcolor: 'white',
                borderRadius: 2, p: 1, display: 'flex',
                alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
              }}>
                {stage.icon}
              </Box>
              <Box>
                <Typography variant="body1" fontWeight={700} sx={{ color: stage.color }}>
                  Current Stage: {stage.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stage.description}
                </Typography>
              </Box>
              <Chip
                label={`Step ${currentIndex + 1} of ${STAGES.length}`}
                size="small"
                sx={{ ml: 'auto', bgcolor: stage.color, color: 'white', fontWeight: 700 }}
              />
            </Paper>
          );
        })()}
      </Box>

      {/* Stage Stepper */}
      <Stepper activeStep={currentIndex} orientation="vertical">
        {STAGES.map((stage, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isNext = index === currentIndex + 1;
          const isFuture = index > currentIndex;

          return (
            <Step key={stage.key} expanded>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: isCompleted ? stage.color : isCurrent ? stage.color : '#e0e0e0',
                    '& .MuiSvgIcon-root': { fontSize: 28 }
                  }
                }}
                sx={{ cursor: 'default' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5,
                    bgcolor: isCompleted || isCurrent ? stage.bg : '#f5f5f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: isCompleted || isCurrent ? stage.color : '#bbb',
                    transition: 'all 0.3s'
                  }}>
                    {stage.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={isCurrent ? 700 : 500}
                      sx={{ color: isCurrent ? stage.color : isCompleted ? '#333' : '#9e9e9e' }}
                    >
                      {stage.label}
                      {isCurrent && (
                        <Chip label="Active" size="small"
                          sx={{ ml: 1, bgcolor: stage.color, color: 'white', fontSize: 10, height: 18, fontWeight: 700 }}
                        />
                      )}
                      {isCompleted && (
                        <CheckCircle sx={{ ml: 1, fontSize: 14, color: '#2e7d32', verticalAlign: 'middle' }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{stage.description}</Typography>
                  </Box>
                </Box>
              </StepLabel>
              <StepContent>
                {isNext && (
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <Tooltip title="Advance to this stage and notify team">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleAdvanceStage(stage.key)}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={12} /> : stage.icon}
                        sx={{
                          bgcolor: stage.color, textTransform: 'none',
                          fontWeight: 600, borderRadius: 2,
                          '&:hover': { bgcolor: stage.color, opacity: 0.9 }
                        }}
                      >
                        {saving ? 'Advancing...' : `Advance to ${stage.label}`}
                      </Button>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Team members will be notified by email.
                    </Typography>
                  </Box>
                )}
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
}
